import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signUp: (email: string, password: string, fullName: string, referralCode?: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [checkedDeletedFor, setCheckedDeletedFor] = useState<string | null>(null);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // If user chose not to stay logged in, sign out when browser closes
    const handleBeforeUnload = () => {
      if (sessionStorage.getItem('session_only') === 'true') {
        supabase.auth.signOut();
        sessionStorage.removeItem('session_only');
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      subscription.unsubscribe();
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Separate effect to check deleted accounts — only once per user id
  useEffect(() => {
    if (!user || checkedDeletedFor === user.id) return;
    setCheckedDeletedFor(user.id);

    supabase
      .from('profiles')
      .select('deleted_at')
      .eq('id', user.id)
      .single()
      .then(({ data }) => {
        if (data?.deleted_at) {
          toast({
            title: 'Account verwijderd',
            description: 'Dit account is gemarkeerd voor verwijdering en kan niet meer worden gebruikt.',
            variant: 'destructive',
          });
          supabase.auth.signOut();
          setSession(null);
          setUser(null);
        }
      });
  }, [user?.id, checkedDeletedFor]);

  const signUp = async (email: string, password: string, fullName: string, referralCode?: string) => {
    const metadata: Record<string, string> = { full_name: fullName };
    if (referralCode) metadata.referred_by = referralCode;
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata,
        emailRedirectTo: window.location.origin,
      },
    });
    return { error: error as Error | null };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ session, user, loading, signUp, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
